import { titleCase } from '@nsb/core';

/**
 * A tree as every drawing of it reads it (design study 12, 2026-10-06: "the client view of the tree, and the per
 * project admin tree configuration to all use the same styles that our master has been iterating on, the only thing is
 * the differences in how the per project tree is adopted"). The master tree on its page, a job's on its Process page
 * and the client's on their home each turn their own data into these, and one board, one tile and one opened chain
 * draw them (TreeBoard, Tile, FocusChain, PhoneRows).
 */

/** Who does a step, as its band says it (study 10, band B): NSB's, a sub's, a supplier's, the client's, outside. */
export type BandTone = 'ink' | 'slate' | 'ochre' | 'bengara' | 'grey' | 'pale';

/** A step's band: its colour, the kind's mark in it for a decision, a gate, a milestone or an end, and its word (none for a gate that happens: its mark alone). */
export interface Band {
  tone: BandTone;
  mark: 'decision' | 'gate' | 'milestone' | 'end' | null;
  word: string | null;
}

/** Where a step of a job stands (study 12, states A): done, under way, next, waiting on the client, not started. */
export type Standing = 'done' | 'now' | 'next' | 'yours' | 'ahead' | 'override' | 'failed';

export interface TileStep {
  /** Its key in the drawing (a tree key on the master, a step's id on a job). */
  key: string;
  phase: string | null;
  /** What it waits on, by key in the drawing: every wait; the drawing leaves out what a longer way already draws. */
  needs: string[];
  end?: 'start' | 'end';
  /** Its name as stored; drawn in Title Case (Jason, 2026-10-06). */
  title: string;
  band: Band | null;
  picture: string | null;
  /** On a job: where it stands, and the words at its foot ("Done · Mar 30"). */
  standing?: Standing | null;
  foot?: string;
  /**
   * On a job: what holds it back, named for its hover where its foot reads Held (L-17 B): "Held: Construction
   * Authorized, two steps back, is not done yet: it waits on the client." (tree/job.ts `heldLine`). The board's tile and
   * a phone's row show it alike.
   */
  hold?: string;
  /**
   * False for a step not configured yet (Jason, 2026-10-07: "clearly highlight unconfigured steps in any tree and always
   * show its icon"): drawn as any step is, its picture and all, and marked plainly, "Not configured" at its foot and a
   * ring (look.ts). On every tree NSB reads: the master's steps by the process tree page's rule (core
   * `builderCompleteness`), a job's by its master node in the tree the job reads (core `lockedCompleteness`) or, until it
   * is done and while the job is not kept, a special task it carries without what it needs set; never on the client's
   * sheet.
   */
  configured?: boolean;
  /** How much of it is set, for the bar along its foot (the master). */
  complete?: { done: number; of: number };
  /**
   * On a job: a deviation (design study 21), a step come late, offered or on the job and not done, or a gate marked done
   * before the rest of its phase and not yet confirmed. Ringed dashed in bengara, its foot in bengara saying so (look.ts).
   */
  deviation?: boolean;
  /**
   * On a job: it sits outside its own phase, come late into the phase under way (design study 21, land A), offered or
   * added. Its lines to and from steps of earlier phases are drawn straight across the transitions, in bengara: "A late
   * step’s lines are the only ones that cross a transition" (transitions.ts `isLateLine`).
   */
  late?: boolean;
  /** On a job: a step not on it, drawn where it would go: its dot, or the dot opened (design study 18). */
  ghost?: boolean;
  /**
   * On a job: a step left out of it, a dot in the line where it would have been with its name crossed out (design
   * study 18); clicked, it is shown with + Add and the filing box.
   */
  dot?: boolean;
  /** Drawn full, never greyed: setting a job's process up, where what matters is what it has, not where it stands. */
  full?: boolean;
  /** A dot with steps after it: the line runs on through it. */
  through?: boolean;
  /** While the master is joined up: nothing comes into it (red), or it leads to nothing (blue). */
  highlight?: 'loose' | 'dead' | null;
  /** A decision's ways on, each a handle along its foot a line is drawn from (the master). */
  options?: { option: string; text: string }[] | null;
  /**
   * On a job, NSB's and the client's: a task done in an earlier phase than its step (design study 31, slice 4; core
   * `earlyTasks`), drawn in that phase as a small tile, "For Engineering" under its name (`earlyFor`) and where it
   * stands at its foot in words, as every tile's ("Under way", "Held", "Waiting on you"). Its own
   * waits are its needs (where it has none, the gate that opens its phase: `opensFrom`), and its step waits on it in its
   * place, so its line runs on to its step through the gates as every line does. Opened, it opens its step (TreeView),
   * where it is in the list; never found apart from it.
   */
  early?: { stepKey: string; stepName: string };
  /** What it says to whoever cannot see it. */
  words: string;
}

/** A phase as a block (study 09): its name, what happens in it, and the facts along its heading. */
export interface TilePhase {
  key: string;
  name: string;
  what: string;
  closedBy: string | null;
  facts: string[];
}

/** A wait drawn: `dst` waits on `src`, or on one way on of it (a decision's option). */
export interface TileLine {
  src: string;
  dst: string;
  option: string | null;
  /** A late step's line back to an earlier phase, drawn across the transitions in bengara (transitions.ts `isLateLine`). */
  late?: boolean;
}

/** A step's name as every drawing shows it: Title Case (Jason, 2026-10-06: "all of these node titles should be title case everywhere"). */
export const stepName = (title: string): string => titleCase(title);

/** What a tile says to whoever cannot see it: its name, its band's word, and the rest, each a sentence. */
export const tileWords = (title: string, band: Band | null, ...rest: string[]): string =>
  [stepName(title), band?.word ?? null, ...rest].filter((x): x is string => Boolean(x)).map((x) => `${x}.`).join(' ');

/**
 * A task done early's line under its name: the step it is done for (design study 31, §7: "For Engineering"), fed its
 * step's name as drawn (`early.stepName`). Where it stands is at its foot, in words, as every tile's is.
 */
export const earlyFor = (stepName: string): string => `For ${stepName}`;

/**
 * The step drawn as opening a phase, by its key in the drawing: the gate closing the latest phase before it that has one
 * drawn (`closedBy`), else the start. A task done early with no waits of its own hangs from it (design study 31, slice
 * 4: "hanging from the gate that opens that phase"), as the engine holds it (core `holdOrder`); its line then runs
 * through the gates between as every line does (transitions.ts `linesThrough`). None where nothing is drawn before it.
 */
export function opensFrom(phases: readonly Pick<TilePhase, 'key' | 'closedBy'>[], phase: string | null, start: string | null): string | null {
  const at = phases.findIndex((p) => p.key === phase);
  if (at < 0) return null;
  for (let i = at - 1; i >= 0; i--) {
    const gate = phases[i]!.closedBy;
    if (gate) return gate;
  }
  return start;
}

/** Each task drawn early by its key in the drawing, with the step it is done for: what a click on it opens (TreeView). */
export const earlySteps = (steps: readonly Pick<TileStep, 'key' | 'early'>[]): Map<string, string> => new Map(steps.flatMap((s) => (s.early ? [[s.key, s.early.stepKey] as const] : [])));

/** The waits as lines, each once: from each step's needs, a need written `key:option` following that option. */
export function linesOf(steps: readonly TileStep[]): TileLine[] {
  const keys = new Set(steps.map((s) => s.key));
  const out: TileLine[] = [];
  const seen = new Set<string>();
  for (const s of steps) {
    for (const need of s.needs) {
      const at = need.lastIndexOf(':');
      const src = at > 0 && !keys.has(need) ? need.slice(0, at) : need;
      const option = src !== need ? need.slice(at + 1) : null;
      if (!keys.has(src) || src === s.key) continue;
      const id = `${s.key}<${src}`;
      if (seen.has(id)) continue;
      seen.add(id);
      out.push({ src, dst: s.key, option });
    }
  }
  return out;
}

/** What a step waits on, by key alone (an option's line is its decision's). */
export const needKeys = (s: Pick<TileStep, 'needs'>, keys: ReadonlySet<string>): string[] =>
  s.needs.map((n) => (keys.has(n) ? n : n.lastIndexOf(':') > 0 ? n.slice(0, n.lastIndexOf(':')) : n)).filter((k) => keys.has(k));

/**
 * A phase opened as its card (the master's, where its facts are set; Jason, 2026-10-07: "Phase facts are edited on a
 * card opened from the phase heading"), kept where a step's key is kept: the page's `open`.
 */
export const phaseCardKey = (phase: string): string => `phase:${phase}`;

/** The phase a key opened as its card names, or none (a step's key). */
export const cardPhase = (key: string | null | undefined): string | null => (key?.startsWith('phase:') ? key.slice('phase:'.length) : null);
